"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { STAFF_PIN_PATTERN, verifyMyStaffPin } from "@/api/user/staff-pin";
import { authService } from "@/services/auth-service";

/** Shared PIN-entry logic for the in-person gates (the Done overlay and the
 *  other-tab return gate): verify against the server, count wrong guesses, sign
 *  out on lockout, and offer a manual sign-out. Keeps both gates identical. */
export function useStaffPinUnlock(onUnlocked) {
  const $ = _c(15);
  const router = useRouter();
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState(null);
  const signOutStarted = useRef(false);
  let t0;
  if ($[0] !== router) {
    t0 = async () => {
      if (signOutStarted.current) {
        return;
      }
      signOutStarted.current = true;
      setSigningOut(true);
      try {
        await authService.logout();
        router.push("/login");
      } catch {
        signOutStarted.current = false;
        setSigningOut(false);
        setError("Could not sign out. Please try again.");
      }
    };
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const signOut = t0;
  let t1;
  if ($[2] !== checking || $[3] !== onUnlocked || $[4] !== pin || $[5] !== signOut) {
    t1 = async e => {
      e.preventDefault();
      if (!STAFF_PIN_PATTERN.test(pin) || checking || signOutStarted.current) {
        return;
      }
      setChecking(true);
      setError(null);
      const result = await verifyMyStaffPin(pin);
      setChecking(false);
      setPin("");
      if (signOutStarted.current) {
        return;
      }
      if (!result) {
        setError("Could not check your PIN. Please try again.");
        return;
      }
      if (result.valid) {
        const unlocked = await onUnlocked();
        if (unlocked === false) {
          setError("Couldn't return just yet. Please try again.");
        }
        return;
      }
      if (result.locked) {
        setError("Too many incorrect attempts. Signing out\u2026");
        await signOut();
        return;
      }
      const left = result.attemptsRemaining;
      setError(`Incorrect PIN. ${left} ${left === 1 ? "attempt" : "attempts"} left before sign out.`);
    };
    $[2] = checking;
    $[3] = onUnlocked;
    $[4] = pin;
    $[5] = signOut;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const submit = t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = value => {
      setPin(value);
      setError(null);
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onPinChange = t2;
  let t3;
  if ($[8] !== checking || $[9] !== error || $[10] !== pin || $[11] !== signOut || $[12] !== signingOut || $[13] !== submit) {
    t3 = {
      pin,
      onPinChange,
      error,
      checking,
      signingOut,
      submit,
      signOut
    };
    $[8] = checking;
    $[9] = error;
    $[10] = pin;
    $[11] = signOut;
    $[12] = signingOut;
    $[13] = submit;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}